import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { EventForm } from '../components/EventForm';
import EventPreview from '../components/EventPreview';
import { eventService } from '../services/api';
import styles from './CreateEventPage.module.css';
import { 
  IconConfetti, 
  IconStars, 
  IconCalendarEvent, 
  IconCheck
} from '@tabler/icons-react';
import { ROUTES } from '../constants/routes';

export function CreateEventPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showConfetti, setShowConfetti] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formProgress, setFormProgress] = useState(0);
  const [preselectedOrgId, setPreselectedOrgId] = useState<number | null>(null);
  const [formData, setFormData] = useState({});
  const [showPreview, setShowPreview] = useState(false);

  // Extraire l'orgId de l'URL si présent
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orgIdParam = params.get('orgId');
    if (orgIdParam) {
      const orgId = parseInt(orgIdParam, 10);
      if (!isNaN(orgId)) {
        setPreselectedOrgId(orgId);
        console.log('ID d\'organisation présélectionné:', orgId);
      }
    }
  }, [location]);
  
  const handleFormSubmit = async (formData: any) => {
    try {
      // Utiliser l'ID d'organisation présélectionné si disponible et non déjà spécifié
      const { subcategory, coordinates, ...formDataWithoutSubcategory } = formData;
      
      const eventData = {
        ...formDataWithoutSubcategory,
        // Extract latitude and longitude from coordinates object
        latitude: coordinates?.lat,
        longitude: coordinates?.lon,
        // Remove coordinates object as backend doesn't expect it
        organizationId: formData.organizationId || preselectedOrgId || null
      };
      
      console.log('Données d\'événement à envoyer:', eventData);
      
      // Vérifier si l'organizationId est défini et valide
      if (!eventData.organizationId || isNaN(eventData.organizationId)) {
        throw new Error("L'ID d'organisation est requis et doit être un nombre valide");
      }
      
      // Activer l'effet de confettis pour célébrer la création
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
      
      const result = await eventService.create(eventData);
      console.log('Événement créé avec succès:', result);
      
      // Rediriger vers la page d'accueil au lieu de la page de détails
      // Cela évite les problèmes liés à l'ID d'événement
      navigate(ROUTES.HOME);
      
      return result;
    } catch (error) {
      console.error('Erreur lors de la création de l\'événement:', error);
      throw error;
    }
  };

  const handleStepChange = (step: number) => {
    setCurrentStep(step);
    // Calculate progress based on current step
    setFormProgress((step - 1) / 2 * 100);
  };

  const handleFormDataChange = (updatedFormData: any) => {
    setFormData(updatedFormData);
  };

  const togglePreview = () => {
    setShowPreview(prev => !prev);
  };

  return (
    <div className={styles.createEventPage}>
      {showConfetti && (
        <div className={styles.confettiContainer}>
          <IconConfetti size={32} className={styles.confetti1} />
          <IconConfetti size={24} className={styles.confetti2} />
          <IconStars size={28} className={styles.confetti3} />
          <IconConfetti size={20} className={styles.confetti4} />
          <IconStars size={32} className={styles.confetti5} />
        </div>
      )}
      
      <div className={styles.hero}>
        <div className={styles.heroIcon}>
          <IconCalendarEvent size={48} />
        </div>
        <h1>Créer un nouvel événement</h1>
        <p>Donnez vie à vos idées et partagez-les avec la communauté étudiante !</p>
      </div>

      <div className={styles.contentContainer}>
        <div className={styles.formContainer}>
          <div className={styles.stepsContainer}>
            <div className={`${styles.step} ${currentStep >= 1 ? styles.active : ''} ${currentStep > 1 ? styles.completed : ''}`}>
              <div className={styles.stepNumber}>
                {currentStep > 1 ? <IconCheck size={16} /> : 1}
              </div>
              <div className={styles.stepText}>Informations de base</div>
            </div>
            <div className={styles.stepDivider}></div>
            <div className={`${styles.step} ${currentStep >= 2 ? styles.active : ''} ${currentStep > 2 ? styles.completed : ''}`}>
              <div className={styles.stepNumber}>
                {currentStep > 2 ? <IconCheck size={16} /> : 2}
              </div>
              <div className={styles.stepText}>Lieu et horaire</div>
            </div>
            <div className={styles.stepDivider}></div>
            <div className={`${styles.step} ${currentStep === 3 ? styles.active : ''}`}>
              <div className={styles.stepNumber}>3</div>
              <div className={styles.stepText}>Détails et publication</div>
            </div>
          </div>

          <div className={styles.progressIndicator}>
            <div 
              className={styles.progressBar} 
              style={{ width: `${formProgress}%` }}
            ></div>
          </div>

          <EventForm 
            onSubmit={handleFormSubmit} 
            onStepChange={handleStepChange}
            currentStep={currentStep}
            preselectedOrganizationId={preselectedOrgId}
            onFormDataChange={handleFormDataChange}
          />
        </div>

        <EventPreview 
          formData={formData} 
          visible={showPreview}
          onToggleVisibility={togglePreview}
        />
      </div>
    </div>
  );
}

export default CreateEventPage;
